<script setup lang="ts">
  import { computed } from 'vue';
  import { Tree, TreeItem, TreeItemLabel } from '@/shared/ui';
  import { cn } from '@/shared/lib/utils';
  import { DIRECTORY_TYPE_ICONS } from '../config/directoryTypeIcons';
  import { getDirectoryModuleIcon } from '@/shared/ui/module-icons';
  import { isMovableNode } from '../model/directoryTree.utils';
  import { DIRECTORY_ITEM_LABEL_SEPARATOR } from '../model/directoryItemLabel';
  import type { DirectoryTreeNode } from '../model/types';

  const props = defineProps<{
    items: DirectoryTreeNode[];
    expandedIds: string[];
    selectedNodeId: string;
    selectedNodeIds?: string[];
    dragOverNodeId?: string;
    indent?: number;
    getKey: (node: DirectoryTreeNode) => string;
    getChildren: (node: DirectoryTreeNode) => DirectoryTreeNode[] | undefined;
  }>();

  const emit = defineEmits<{
    (e: 'update:expandedIds', value: string[]): void;
    (e: 'select', id: string, multiSelect: boolean): void;
    (e: 'node-dragstart', event: DragEvent, node: DirectoryTreeNode): void;
    (e: 'node-dragover', event: DragEvent, node: DirectoryTreeNode): void;
    (e: 'node-dragleave', event: DragEvent, node: DirectoryTreeNode): void;
    (e: 'node-drop', event: DragEvent, node: DirectoryTreeNode): void;
    (e: 'node-dragend', event: DragEvent): void;
  }>();

  const expandedModel = computed({
    get: () => props.expandedIds,
    set: (value: string[]) => emit('update:expandedIds', value),
  });

  const asNode = (value: DirectoryTreeNode) => value;

  const MODIFIER_KEYS = new Set(['Control', 'Meta', 'Shift', 'Alt']);

  const onTreeKeydownCapture = (event: KeyboardEvent) => {
    if (MODIFIER_KEYS.has(event.key) || event.ctrlKey || event.metaKey) {
      event.stopPropagation();
    }
  };

  const isNodeSelected = (id: string) =>
    props.selectedNodeId === id || (props.selectedNodeIds?.includes(id) ?? false);

  const isNodeDraggable = (node: DirectoryTreeNode) => isMovableNode(node);

  const isDropTarget = (id: string) => props.dragOverNodeId === id;

  const hasExpandArrow = (node: DirectoryTreeNode, hasChildren: boolean) =>
    node.kind === 'folder' ? true : hasChildren;

  const nodeIcon = (node: DirectoryTreeNode) => {
    if (node.kind === 'module') {
      return getDirectoryModuleIcon(node.details?.iconId);
    }
    if (node.kind === 'folder') {
      return DIRECTORY_TYPE_ICONS.folder;
    }
    if (node.kind === 'matrix') {
      return DIRECTORY_TYPE_ICONS.matrix;
    }
    if (node.kind === 'record' || node.kind === 'generatedRecord') {
      return DIRECTORY_TYPE_ICONS.record;
    }
    return DIRECTORY_TYPE_ICONS.document;
  };

  // Documento, registro e matriz renderizam o mesmo formato, a partir dos campos que o nó já
  // guarda separados. Antes o prefixo era reparseado do rótulo e só `file`/`record` passavam por
  // aqui, então documento e registro apareciam em formatos diferentes na mesma árvore.
  const hasSplitLabel = (node: DirectoryTreeNode) => Boolean(node.prefix && node.title);

  const labelSeparator = DIRECTORY_ITEM_LABEL_SEPARATOR;

  const nodeIconClass = (node: DirectoryTreeNode) => {
    if (node.kind === 'folder') {
      return 'text-[var(--color-nude)]';
    }
    return 'text-gray-07';
  };

  const nodeIconSizeClass = (node: DirectoryTreeNode) =>
    node.kind === 'module' ? 'size-[22px]' : 'size-6';

  const nodeLabelClass = (node: DirectoryTreeNode) =>
    node.kind === 'module' || node.kind === 'folder' ? 'font-medium' : '';
</script>

<template>
  <div
    data-testid="directories-tree"
    class="w-full py-2"
    @keydown.capture="onTreeKeydownCapture"
  >
    <Tree
      v-model:expanded="expandedModel"
      :items="items"
      :get-key="getKey"
      :get-children="getChildren"
      :indent="indent ?? 20"
    >
      <template #default="{ flattenItems, indent: treeIndent }">
        <TreeItem
          v-for="item in flattenItems"
          :key="item._id"
          :value="asNode(item.value)"
          :level="item.level"
          :indent="treeIndent"
          class="block w-full px-2"
        >
          <template #default="{ isExpanded, handleToggle }">
            <div
              role="treeitem"
              tabindex="0"
              :draggable="isNodeDraggable(asNode(item.value))"
              :class="
                cn(
                  'relative flex w-full cursor-pointer items-center rounded-lg px-2 py-2.5 text-left transition-colors select-none',
                  isNodeSelected(asNode(item.value).id)
                    ? 'bg-beige-xx'
                    : 'hover:bg-beige-xx/50',
                  isDropTarget(asNode(item.value).id) &&
                    'ring-2 ring-[var(--theme-secondary-ink)] ring-inset'
                )
              "
              @click="emit('select', asNode(item.value).id, $event.ctrlKey || $event.metaKey)"
              @keydown.enter.prevent="emit('select', asNode(item.value).id, false)"
              @keydown.space.prevent="emit('select', asNode(item.value).id, false)"
              @dragstart="emit('node-dragstart', $event, asNode(item.value))"
              @dragover="emit('node-dragover', $event, asNode(item.value))"
              @dragleave="emit('node-dragleave', $event, asNode(item.value))"
              @drop="emit('node-drop', $event, asNode(item.value))"
              @dragend="emit('node-dragend', $event)"
            >
              <TreeItemLabel
                :has-children="hasExpandArrow(asNode(item.value), item.hasChildren)"
                :is-expanded="isExpanded"
                class="w-full"
                @toggle="handleToggle"
              >
                <span class="flex min-w-0 items-center gap-2">
                  <component
                    :is="nodeIcon(asNode(item.value))"
                    class="shrink-0"
                    :class="[nodeIconSizeClass(asNode(item.value)), nodeIconClass(asNode(item.value))]"
                  />
                  <span
                    v-if="hasSplitLabel(asNode(item.value))"
                    class="truncate text-xs text-gray-07"
                    :class="nodeLabelClass(asNode(item.value))"
                  >
                    <span class="text-gray-07">{{ asNode(item.value).prefix }}</span
                    >{{ labelSeparator }}{{ asNode(item.value).title }}
                  </span>
                  <span
                    v-else
                    class="truncate text-xs text-gray-07"
                    :class="nodeLabelClass(asNode(item.value))"
                  >{{ asNode(item.value).name }}</span>
                </span>
              </TreeItemLabel>
            </div>
          </template>
        </TreeItem>
      </template>
    </Tree>
  </div>
</template>
